import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow, PageHeader } from "../components/bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ShieldCheck, Battery, FileLock, Radio } from "lucide-react";
import { CERTIFICATIONS } from "../data/content";

const STATUS_STYLES = {
  Certified: "bg-revo-green text-navy",
  "In testing": "bg-revo-green/15 text-revo-green border border-revo-green/40",
  Planned: "bg-white/5 text-revo-ice/60 border border-white/15",
};

const SAFETY_FAQ = [
  { q: "Is it safe to sit near?", a: "Surfaces use the same certified induction as the Qi chargers already in a billion devices, active only under an authenticated device. Ambient units operate inside FCC human-exposure limits." },
  { q: "What about pacemakers and pets?", a: "As with existing wireless chargers, we'll publish specific medical-implant guidance with each product's certification as it completes testing." },
  { q: "Can it power a device it doesn't recognize?", a: "No. Transmitters deliver power only to authenticated, enrolled receivers — keys, cards, and coffee cups on the desk receive nothing." },
  { q: "What stops overheating?", a: "Foreign-object detection, thermal monitoring, and instant shutoff are requirements of the Qi2 and Ki standards we build on — not features we bolted on." },
];

export default function Technology() {
  return (
    <Layout>
      <PageHeader
        eyebrow="Technology & safety"
        title="Standards first. Claims second."
        subtitle="Revo surfaces are built on Qi2, the Wireless Power Consortium standard already inside roughly a billion devices. Revo Kitchen is built on Ki, the WPC's 2,200-watt cordless kitchen standard. Ambient units use FCC-authorized RF and infrared power inside human-safety exposure limits."
      />

      {/* CERT TRACKER */}
      <section className="bg-navy px-5 py-20 md:px-8">
        <div className="mx-auto max-w-4xl">
          <Reveal><Eyebrow>Certification tracker</Eyebrow><h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Live status, never overstated.</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 overflow-hidden rounded-3xl border border-white/10" data-testid="cert-tracker">
              {CERTIFICATIONS.map((c, i) => (
                <div key={c.name} className={`flex items-center justify-between gap-4 px-6 py-5 ${i ? "border-t border-white/10" : ""}`}>
                  <div>
                    <p className="font-display text-lg font-extrabold text-white">{c.name}</p>
                    <p className="text-xs text-revo-ice/50">{c.body}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${STATUS_STYLES[c.status]}`} data-testid={`cert-status-${c.name.replace(/\s+/g, "-")}`}>{c.status}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SAFETY PILLARS */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-5 md:grid-cols-2">
            {[
              [ShieldCheck, "Safety by design", "Transmitters deliver power only to authenticated, enrolled receivers — keys, cards, and coffee cups on the desk receive nothing."],
              [Battery, "Battery health, managed", "ChargeRevo's cloud keeps every battery in its healthy charge band with shallow cycles — so your batteries age slower than they do on wall chargers."],
              [Radio, "Certified emissions", "Ambient units use FCC-authorized RF and infrared power operating inside human-safety exposure limits, published as each product completes testing."],
              [FileLock, "Patent pending", "ChargeRevo's serial-based entitlement, per-device metering, and subscription billing system for wireless power delivery."],
            ].map(([Icon, t, d], i) => (
              <Reveal key={t} delay={(i % 2) * 0.1}>
                <div className="h-full rounded-3xl border border-navy/10 bg-revo-surface p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-revo-green"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-xl font-extrabold text-navy">{t}</h3>
                  <p className="mt-2 leading-relaxed text-navy/60">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INVERSE SQUARE */}
      <section className="relative overflow-hidden bg-navy px-5 py-24 md:px-8 grain">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Eyebrow>The honest physics</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-black leading-tight text-white sm:text-4xl">Double the distance, quarter the power.</h2>
            <div className="mt-10 flex items-end justify-center gap-6">
              {[[1, "1m", "100%"], [2, "2m", "25%"], [3, "3m", "11%"]].map(([d, dist, pwr]) => (
                <div key={d} className="flex flex-col items-center gap-3">
                  <div className="flex items-end" style={{ height: 160 }}>
                    <div className="w-16 rounded-t-xl bg-revo-green" style={{ height: `${100 / (d * d)}%`, minHeight: 8 }} />
                  </div>
                  <p className="font-display text-lg font-bold text-white">{pwr}</p>
                  <p className="text-xs text-revo-ice/60">at {dist}</p>
                </div>
              ))}
            </div>
            <p className="mx-auto mt-10 max-w-2xl text-lg text-revo-ice">
              That's why ChargeRevo's answer is coverage, not beams — full power on surfaces, gentle top-up everywhere else.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SAFETY FAQ */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto max-w-3xl">
          <Reveal><Eyebrow dark>Safety FAQ</Eyebrow><h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">The questions we get asked most.</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8" data-testid="safety-faq">
              {SAFETY_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`s${i}`} className="border-navy/10">
                  <AccordionTrigger className="text-left font-display text-lg font-bold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-navy/70">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
